
import type { FeatureNode } from '../../models'
import type { FeatureConfig, FeatureConfigProvider } from '../../providers/interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { defineComponent, getCurrentInstance, ref } from 'vue'
import { mapGetters } from 'vuex'
import { useExplainBrowse } from '../../composables/explain/use-explain-browse'
import { useOptimizeMenu } from '../../composables/use-optimize-menu'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum } from '../../enums'
import { APP_CONSTS } from '../../store/app/app.consts'
import { FeaturesTreeUtils } from '../../utils/features-tree.utils'
import { UiTooltip } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'

export default defineComponent({
  name: 'ExplainOptimizeMenu',
  components: {
    EpbButton,
    UiTooltip,
  },
  props: {
    query: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $router },
    } = getCurrentInstance()

    const { isBrowseContext } = useExplainBrowse()
    const { getLinkRoute } = useOptimizeMenu()
    const loading = ref<string | undefined>(undefined)

    const isFeatureBeingOpenedInNewTab = (feature: FeatureNode): boolean =>
      loading.value === feature.id

    const openFeatureInNewTab = async (feature: FeatureNode): Promise<void> => {
      loading.value = feature.id
      const routeObj = await getLinkRoute(feature, props.query)
      const route = $router.resolve(routeObj)
      window.open(route.href, '_blank')
      loading.value = undefined
    }

    return {
      loading,
      isBrowseContext,
      isFeatureBeingOpenedInNewTab,
      openFeatureInNewTab,
    }
  },
  data() {
    return {
      baseClass: 'epb-explain-optimize-menu',
      availableFeaturesSettings: [
        FeatureIdEnum.EQUALIZE,
        FeatureIdEnum.SYNONYM,
        FeatureIdEnum.CONFIGURATION,
      ],
      itemsExpanded: false,
    }
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    availableFeaturesSettingsToDisplay(): FeatureIdEnum[] {
      return this.isBrowseContext
        ? this.availableFeaturesSettings.filter(featureId => featureId !== FeatureIdEnum.SYNONYM)
        : this.availableFeaturesSettings
    },
    availableFeatures(): Array<FeatureNode & FeatureConfig> {
      const getFeatureConfig = CONTAINER.get<FeatureConfigProvider>(TYPES.FeatureConfigProvider)
      return this.availableFeaturesSettingsToDisplay.reduce(
        (features: Array<FeatureNode & FeatureConfig>, featureId: FeatureIdEnum) => {
          const featureNode = FeaturesTreeUtils.getFeatureNode(
            featureId,
            this.featuresTree[DomainEnum.SEARCH],
          )

          if (featureNode) {
            features.push({
              ...featureNode,
              ...getFeatureConfig(featureId),
            })
          }

          return features
        },
        [],
      )
    },
    isVisible(): boolean {
      return this.availableFeatures.length > 0
    },
  },
})
